import { formatDate, parseDate } from '@travelata-libs/utils';
import type { ApiTourCriteria, MinPricesResponse } from './types';
import { useTravelataApiService } from '@/shared/composables/useTravelataApiService';
import { useTravelataAppConfig } from '@/shared/composables/useTravelataAppConfig';
import type { SearchCriteriaParams } from '@/shared/model';

const ENDPOINT = '/frontend/prices/searchMinPriceByCriterias';

const DEFAULT_PRICE_RANGE = { from: 6000, to: 21000000 } as const;

const API_DATE_FORMAT = 'yyyy-MM-dd';

export function toApiDate(value: string): string {
  const parsed = parseDate(value);
  return parsed ? formatDate(parsed, API_DATE_FORMAT) : value;
}

export function buildTourCriteria(history: SearchCriteriaParams, index: number): ApiTourCriteria {
  return {
    checkInDateRange: {
      from: toApiDate(history.checkInDateRange?.from ?? ''),
      to: toApiDate(history.checkInDateRange?.to ?? ''),
    },
    priceRange: { ...DEFAULT_PRICE_RANGE },
    nightRange: {
      from: history.nightRange?.from,
      to: history.nightRange?.to,
    },
    meals: [],
    hotelCategories: [],
    touristGroup: {
      adults: history.touristGroup?.adults ?? 0,
      kids: history.touristGroup?.kids ?? 0,
      infants: history.touristGroup?.infants ?? 0,
    },
    departureCity: history.departureCity?.id,
    countries: history.countries ?? [],
    resorts: history.resorts ?? [],
    hotels: history.hotels ?? [],
    meal: [],
    hotelCategory: [],
    index,
  };
}

function buildBody(criterias: ApiTourCriteria[]): URLSearchParams {
  const body = new URLSearchParams();
  criterias.forEach((criteria) => {
    body.append('tourCriterias[]', JSON.stringify(criteria));
  });
  return body;
}

function readIndex(criteria: string): number | null {
  try {
    const parsed = JSON.parse(criteria) as { index?: unknown };
    return typeof parsed.index === 'number' ? parsed.index : null;
  } catch {
    return null;
  }
}

/**
 * POST минимальных цен по критериям истории.
 *
 * Возвращает маппинг `index → minPrice`, где `index` — позиция записи в `history`
 * (та, с которой критерий ушёл в запрос). Только валидные цены (положительные, с
 * распознанным индексом в диапазоне `history`).
 *
 * Обогащение самих записей истории полем `price` намеренно вынесено на уровень
 * выше (потребитель): эта функция не знает о форме записи за пределами
 * `SearchCriteriaParams` (например, о `SearchCriteriaHistory`).
 *
 * Деградирует молча: пустой вход, ошибка сети, `success !== true`, не-массив
 * `result` → пустой объект (без выброса).
 */
export async function getMinPricesByCriterias(history: SearchCriteriaParams[]): Promise<Record<number, number>> {
  const pricesByIndex: Record<number, number> = {};

  if (!history.length) {
    return pricesByIndex;
  }

  try {
    const apiService = useTravelataApiService();
    const config = useTravelataAppConfig();
    const url = `${config.apiGatewayUrl}${ENDPOINT}`;

    const body = buildBody(history.map((item, index) => buildTourCriteria(item, index)));

    const response = await apiService.post<MinPricesResponse, URLSearchParams>(url, body);
    const result = response?.result;
    if (response?.success !== true || !Array.isArray(result) || !result.length) {
      return pricesByIndex;
    }

    result.forEach((entry) => {
      const index = readIndex(entry?.criteria ?? '');
      const minPrice = entry?.minPrice;
      if (index !== null && index >= 0 && index < history.length && typeof minPrice === 'number' && minPrice > 0) {
        pricesByIndex[index] = minPrice;
      }
    });

    return pricesByIndex;
  } catch {
    return pricesByIndex;
  }
}
